import { version as engineVersion } from '../version';

/**
 * Sent to `window.parent` after each `System.init` completes during `Game.addSystem`
 * (when running inside an iframe and parent differs from self).
 */
export const COMBOS_GAME_PLUGIN_INIT_SUCCESS =
  "combos-game:plugin-init-success" as const;

export interface CombosGamePluginInitSuccessMessage {
  type: typeof COMBOS_GAME_PLUGIN_INIT_SUCCESS;
  /** Resolved from `System.systemName` on the constructor, or `"UnknownSystem"`. */
  systemName: string;
  /** `@combos-fun/engine` build version hosting this system. */
  engineVersion: string;
  /** npm package name when the System class sets `static packageName`. */
  packageName?: string;
  /** semver from `static packageVersion` on the System class. */
  packageVersion?: string;
}

export interface CombosGamePluginInitSuccessInput {
  systemName: string;
  packageName?: string;
  packageVersion?: string;
}

/**
 * iframe → parent: game bootstrap finished (all systems `init`/`awake`, optional
 * scene load and `start`). Sent once, even when `autoStart` is `false`, so the host
 * knows assets + scene graph are ready and it may pause / hold before play.
 */
export const COMBOS_GAME_READY = "combos-game:ready" as const;

/**
 * parent → iframe: start / pause the game loop. Payload: `{ playing: boolean }`.
 * `true` cold-starts (`Game.start`) on first play, otherwise resumes; `false` pauses.
 */
export const COMBOS_GAME_SET_PLAYING = "combos-game:set-playing" as const;

/**
 * iframe → parent: current run state after a `start` / `pause` / `resume`.
 */
export const COMBOS_GAME_STATE_CHANGED = "combos-game:state-changed" as const;

export interface CombosGameReadyMessage {
  type: typeof COMBOS_GAME_READY;
  engineVersion: string;
  /** Set when bootstrap threw (serialized error message). */
  error?: string;
}

export interface CombosGameStateChangedMessage {
  type: typeof COMBOS_GAME_STATE_CHANGED;
  /** Loop currently running. */
  playing: boolean;
  /** Whether the game has been started at least once (cold start happened). */
  started: boolean;
}

export interface CombosGameSetPlayingMessage {
  type: typeof COMBOS_GAME_SET_PLAYING;
  playing: boolean;
}

function postToParent(payload: object, targetOrigin: string): void {
  if (typeof window === "undefined") return;
  if (!window.parent || window.parent === window) return;
  try {
    window.parent.postMessage(payload, targetOrigin);
  } catch {
    /* ignore cross-origin or detached frame */
  }
}

/**
 * Notifies the embedding page that a system (plugin) finished async/sync `init`.
 */
export function postParentPluginInitSuccess(
  input: CombosGamePluginInitSuccessInput,
  targetOrigin: string = "*",
): void {
  const payload: CombosGamePluginInitSuccessMessage = {
    type: COMBOS_GAME_PLUGIN_INIT_SUCCESS,
    systemName: input.systemName,
    engineVersion,
    ...(input.packageName ? { packageName: input.packageName } : {}),
    ...(input.packageVersion ? { packageVersion: input.packageVersion } : {}),
  };
  postToParent(payload, targetOrigin);
}

/** Notifies the embedding page that game bootstrap finished. */
export function postParentGameReady(
  error?: unknown,
  targetOrigin: string = "*",
): void {
  const payload: CombosGameReadyMessage = {
    type: COMBOS_GAME_READY,
    engineVersion,
    ...(error === undefined
      ? {}
      : { error: error instanceof Error ? error.message : String(error) }),
  };
  postToParent(payload, targetOrigin);
}

/** Notifies the embedding page of the current play/pause state. */
export function postParentGameState(
  state: { playing: boolean; started: boolean },
  targetOrigin: string = "*",
): void {
  const payload: CombosGameStateChangedMessage = {
    type: COMBOS_GAME_STATE_CHANGED,
    playing: state.playing,
    started: state.started,
  };
  postToParent(payload, targetOrigin);
}

/**
 * Reads a `combos-game:set-playing` command from an inbound `postMessage` payload.
 * Returns the requested `playing` value, or `null` when the message is unrelated / malformed.
 */
export function parseSetPlayingMessage(data: unknown): boolean | null {
  if (!data || typeof data !== "object") return null;
  const d = data as { type?: unknown; playing?: unknown };
  if (d.type !== COMBOS_GAME_SET_PLAYING) return null;
  if (typeof d.playing !== "boolean") return null;
  return d.playing;
}
